//bureau/src/pages/UserCreation/BiodataUploadModal

import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import { postWithFormData } from "../../helpers/api.helper";
import { FaUpload, FaImage, FaTimes, FaSpinner } from "react-icons/fa";
import { normalizeProfileData } from "./helpers/creation.helper";
import { ProfileViaData } from "./types/ProfileViaData";
import ProfileViaForm from "./components/ProfileViaForm";

interface BiodataUploadModalProps {
  onClose: () => void;
  bureauId: string;
  isMediatorProfile?: boolean; // Add this prop to detect profile type
}

const BiodataUploadModal: React.FC<BiodataUploadModalProps> = ({
  onClose,
  bureauId,
  isMediatorProfile = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState<ProfileViaData>({
    gender: "",
    createdBy: "",
    citizenship: "Indian",
    mobileNumber: "",
    password: "",
    fullName: "",
    dateOfBirth: "",
    birthTime: "",
    maritalStatus: "",
    height: "",
    religion: "",
    caste: "",
    subCaste: "",
    education: "",
    employment: "",
    occupation: "",
    annualIncome: "",
    fatherOccupation: "",
    motherOccupation: "",
    totalPropertyValue: "",
    profileDescription: "",
    raasi: "",
    nakshatram: "",
    gothram: "",
    placeOfBirth: "",
    country: "",
    state: "",
    district: "",
  });

  const generatePassword = () => {
    const digits = "0123456789";
    let password = "";

    for (let i = 0; i < 8; i++) {
      password += digits.charAt(Math.floor(Math.random() * digits.length));
    }

    return password;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];

      // Validate file size (max 5MB)
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }

      // Validate file type
      if (!selectedFile.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      setFile(selectedFile);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {};
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith("image/")) {
      setFile(droppedFile);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(droppedFile);
    } else {
      toast.error("Please drop a valid image file");
    }
  };

  const parseBackendResponse = (responseData: any): ProfileViaData => {
    const empty: ProfileViaData = {
      gender: "",
      createdBy: isMediatorProfile ? "other_mediator" : "",
      citizenship: "Indian",
      mobileNumber: "",
      password: generatePassword(),
      fullName: "",
      dateOfBirth: "",
      birthTime: "",
      maritalStatus: "neverMarried",
      height: "",
      religion: "",
      caste: "",
      subCaste: "",
      education: "",
      employment: "",
      occupation: "",
      annualIncome: "",
      fatherOccupation: "",
      motherOccupation: "",
      totalPropertyValue: "",
      profileDescription: "",
      raasi: "",
      nakshatram: "",
      gothram: "",
      placeOfBirth: "",
      country: "",
      state: "",
      district: "",
    };

    if (!responseData || typeof responseData !== "object") return empty;

    // -------- helpers ----------
    const asText = (v: any): string => {
      if (v === null || v === undefined) return "";
      if (v instanceof Date) return v.toISOString();
      if (Array.isArray(v)) return v.map(asText).filter(Boolean).join(", ");
      if (typeof v === "object") {
        // common OCR formats: { value: "..." } or { text: "..." }
        if (typeof v.toISOString === "function") {
          try {
            return v.toISOString();
          } catch {
            // ignore
          }
        }
        if (typeof v.toString === "function") {
          const str = v.toString();
          if (str && str !== "[object Object]") return str;
        }
        return asText(v.value ?? v.text ?? v.label ?? "");
      }
      return String(v).trim();
    };

    const normKey = (k: string) =>
      k
        .toLowerCase()
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/[_\-]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    // Flatten nested objects (OCR sometimes returns { personalDetails: {...} })
    const flatten = (obj: any, out: Record<string, any> = {}) => {
      if (!obj || typeof obj !== "object") return out;
      for (const [k, v] of Object.entries(obj)) {
        if (v && typeof v === "object" && !Array.isArray(v)) {
          // keep leaf objects too, but also flatten
          out[k] = v;
          flatten(v, out);
        } else {
          out[k] = v;
        }
      }
      return out;
    };

    const flat = flatten(responseData);

    // Build normalized map: "date of birth" -> value
    const map = new Map<string, any>();
    Object.entries(flat).forEach(([k, v]) => {
      map.set(normKey(k), v);
    });

    const pick = (aliases: string[]) => {
      for (const a of aliases) {
        const v = map.get(normKey(a));
        const txt = asText(v);
        if (txt) return txt;
      }
      return "";
    };

    const parseDob = (value: string) => {
      if (!value) return "";
      // dd-mm-yyyy / dd/mm/yyyy / dd.mm.yyyy
      const m = value.match(/(\d{1,2})[\/\-. ](\d{1,2})[\/\-. ](\d{4})/);
      if (m) {
        const dd = m[1].padStart(2, "0");
        const mm = m[2].padStart(2, "0");
        const yyyy = m[3];
        return `${yyyy}-${mm}-${dd}`;
      }
      // yyyy-mm-dd or yyyy/mm/dd
      const ymd = value.match(/(\d{4})[\/\-. ](\d{2})[\/\-. ](\d{2})/);
      if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
      // already iso
      const iso = value.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (iso) return iso[0];
      return value;
    };

    const normalizeGender = (value: string) => {
      const v = (value || "").toLowerCase();
      if (
        v.includes("male") ||
        v === "m" ||
        v.includes("groom") ||
        v.includes("boy") ||
        v.includes("వరుడు") ||
        v.includes("మగ") ||
        v.includes("పురుష")
      ) {
        return "male";
      }
      if (
        v.includes("female") ||
        v === "f" ||
        v.includes("bride") ||
        v.includes("girl") ||
        v.includes("వధువు") ||
        v.includes("ఆడ") ||
        v.includes("స్త్రీ")
      ) {
        return "female";
      }
      return "";
    };

    // "Born Time & place: 8:14 am,Vijayawada"
    const splitTimeAndPlace = (value: string) => {
      if (!value) return { time: "", place: "" };
      const cleaned = value.replace(/\s+/g, " ").trim();

      // time pattern
      const timeMatch = cleaned.match(
        /(\d{1,2}:\d{2}\s*(am|pm|a\.m\.|p\.m\.)?)/i,
      );
      const time = timeMatch ? timeMatch[1].replace(/\./g, "").trim() : "";

      // place after comma or after time
      let place = "";
      if (cleaned.includes(",")) {
        place = cleaned.split(",").slice(1).join(",").trim();
      } else if (time) {
        place = cleaned.replace(timeMatch?.[0] ?? "", "").trim();
      }

      return { time, place };
    };

    // -------- mapping ----------
    const bornTimePlace = pick([
      "born time & place",
      "born time and place",
      "birth time & place",
      "birth time and place",
    ]);

    const bornSplit = splitTimeAndPlace(bornTimePlace);

    const fullName = pick([
      "full name",
      "name",
      "bride name",
      "groom name",
      "candidate name",
      "name of candidate",
    ]);
    const gender = normalizeGender(
      pick([
        "gender",
        "sex",
        "male",
        "female",
        "bride",
        "groom",
        "మగ",
        "ఆడ",
        "వధువు",
        "వరుడు",
      ]),
    );
    const dob = parseDob(pick(["date of birth", "dob", "birth date"]));
    const birthTime =
      pick(["time of birth", "birth time"]) || bornSplit.time || "";
    const placeOfBirth =
      pick(["place of birth", "birth place"]) || bornSplit.place || "";

    const height = pick(["height"]);
    const religion = pick(["religion", "matham", "మతం"]);
    const caste = pick(["caste", "cast", "కులం"]);
    const subCaste = pick(["sub caste", "subcaste", "sub cast", "sub-caste"]);
    const maritalStatusRaw = pick(["marital status", "maritalstatus"]);

    // star/nakshatra
    const nakshatram = pick(["nakshatram", "nakshatra", "star"]);

    // rashi/raasi
    const raasi = pick(["raasi", "rasi", "rashi"]);

    // gotra
    const gothram = pick(["gotra", "gotram", "gothram"]);

    // education synonyms
    const education = pick([
      "education",
      "highest education",
      "highest educational qualification",
      "educational qualification",
      "qualification",
      "degree",
    ]);

    // occupation/job synonyms
    const occupation = pick([
      "occupation",
      "job",
      "job description",
      "designation",
      "company",
      "work",
    ]);

    const employment = pick([
      "employment",
      "employee",
      "employment status",
      "working",
    ]);

    const annualIncome = pick(["income", "salary", "annual income", "package"]);

    // family
    const fatherOccupation = pick([
      "father occupation",
      "father's occupation",
      "father job",
    ]);

    const motherOccupation = pick([
      "mother occupation",
      "mother's occupation",
      "mother job",
    ]);

    const brothers = pick([
      "brothers",
      "total brothers",
      "siblings",
      "sibling",
    ]);

    const sisters = pick(["sisters", "total sisters"]);

    const mobileNumber = pick(["mobile number", "phone", "contact", "mobile"]);

    const totalPropertyValue = pick([
      "total property value",
      "property value",
      "property",
    ]);

    // -------- result ----------
    const normalizeMaritalStatus = (value: string) => {
      const v = (value || "").toLowerCase();
      if (!v) return "";
      if (v.includes("never") || v.includes("unmarried")) return "neverMarried";
      if (v.includes("divorce")) return "divorced";
      if (v.includes("widow") || v.includes("widower")) return "widow";
      if (v.includes("awaiting")) return "awaitingDivorce";
      if (v.includes("separated")) return "separated";
      if (v.includes("married")) return "married";
      return value;
    };

    return {
      ...empty,
      gender,
      createdBy: isMediatorProfile ? "other_mediator" : "",
      citizenship: pick(["citizenship"]) || "Indian",
      mobileNumber,
      fullName,
      dateOfBirth: dob,
      birthTime,
      maritalStatus: normalizeMaritalStatus(maritalStatusRaw),
      placeOfBirth,
      height,
      religion,
      caste,
      subCaste,
      education,
      employment: employment || (occupation ? "privateEmployee" : ""),
      occupation,
      annualIncome,
      fatherOccupation,
      motherOccupation,
      totalPropertyValue,
      raasi,
      nakshatram,
      gothram,
      country: pick(["country"]),
      state: pick(["state"]),
      district: pick(["district"]),
      profileDescription: pick(["profile description", "about", "description"]),
      password: generatePassword(),
    };
  };

  const handleUpload = async () => {
    if (!file) {
      toast.warn("Please select an image file");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("biodata_image", file);
      formData.append("bureauId", bureauId);
      const res = await postWithFormData(
        "/bureau/profile/biodata-upload",
        formData,
      );
      if (res.success) {
        toast.success("Biodata extracted successfully!");
        let responseData = res.data || {};
        if (Object.keys(responseData).length === 0) {
          responseData = {};
        }
        const extractedData = normalizeProfileData(
          parseBackendResponse(responseData),
        );
        setProfileData(extractedData as ProfileViaData);

        setIsEditing(true);
      } else {
        toast.error(res.message || "Failed to extract biodata");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error uploading biodata image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-4xl my-8 mx-4 shadow-lg max-h-[90vh] overflow-y-auto">
        {!isEditing ? (
          /* Upload Section */
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">
                Upload Biodata Image
                {isMediatorProfile && (
                  <span className="ml-2 text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full">
                    Mediator Profile
                  </span>
                )}
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <FaTimes className="text-gray-600" />
              </button>
            </div>

            {/* Drag and Drop Area */}
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition duration-300 mb-4"
            >
              {imagePreview ? (
                <div className="space-y-4">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-96 mx-auto rounded-lg shadow-md"
                  />
                  <p className="text-sm text-gray-600">
                    Click to change image or drag & drop a new one
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <FaImage className="text-6xl text-gray-400 mx-auto" />
                  <div>
                    <p className="text-lg text-gray-700 font-medium">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      PNG, JPG, JPEG up to 5MB
                    </p>
                  </div>
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />

            {file && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-blue-800 mb-2">
                  Selected File
                </h3>
                <p className="text-sm text-blue-700">
                  <strong>Name:</strong> {file.name}
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Size:</strong> {(file.size / 1024).toFixed(2)} KB
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !file}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-md hover:shadow-lg transition disabled:opacity-50 flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FaUpload />
                    Upload & Extract
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          /* Editable Form Section */
          <ProfileViaForm
            initialData={profileData}
            bureauId={bureauId}
            isMediatorProfile={isMediatorProfile}
            onBack={() => {
              setIsEditing(false);
              setFile(null);
              setImagePreview("");
            }}
            onSuccess={onClose}
          />
        )}
      </div>
    </div>
  );
};

export default BiodataUploadModal;
